/**
 * `AddToHomeScreenChip` — a small dismissible install prompt shown to users
 * on their first authenticated sessions.
 *
 * Rules:
 *   • Never shown when the app is already running in standalone / installed
 *     mode (`display-mode: standalone` or `navigator.standalone === true`).
 *   • Never shown until the user has actually logged in — the landing page
 *     must not push install prompts on strangers.
 *   • Two flavours: Android Chrome uses the native `beforeinstallprompt`
 *     event so we can trigger a one-tap install; iOS Safari has no such
 *     event, so we fall back to a short "Share → Add to Home Screen" guide.
 *   • Dismissal is persisted in `localStorage` (`buildintel_a2hs_dismissed`).
 *     Once dismissed, we don't nag for at least 14 days. If the user later
 *     installs manually, the standalone check kicks in and the chip stays
 *     hidden regardless of the dismissal cookie.
 *   • Never blocks the app — chip sits above the bottom nav and can be
 *     tapped-through around; the "Later" button also dismisses.
 *
 * Wired once in `Layout.jsx` so it renders on top of every authenticated
 * page (Capture, Ledger, etc.) but only the FIRST time it becomes eligible.
 */
import { useEffect, useState } from "react";
import { Share2, Plus, X, Download, Smartphone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Per-user dismissal so a brand-new signup on the same device gets the nudge
// again. The old device-wide key (`buildintel_a2hs_dismissed_at`) is cleaned
// up on first render below so returning users don't inherit a stale flag.
const LEGACY_DEVICE_KEY = "buildintel_a2hs_dismissed_at";
const dismissKeyFor = (userId) =>
  `homeScreenNudgeDismissed:${userId ?? "anon"}`;
const NAG_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS Safari sets a non-standard `navigator.standalone`; treat true as installed.
    window.navigator?.standalone === true
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export default function AddToHomeScreenChip() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  // `deferredPrompt` holds the Android `beforeinstallprompt` event so we can
  // fire it on tap without another user gesture.
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (isStandalone()) return;
    // One-time migration: drop the old device-wide flag so a fresh account on
    // the same browser isn't silently suppressed by a prior user's dismissal.
    try {
      localStorage.removeItem(LEGACY_DEVICE_KEY);
    } catch {
      /* ignore */
    }
    // Respect a recent dismissal — keyed by THIS user's id, not the device.
    try {
      const ts = Number(localStorage.getItem(dismissKeyFor(user.id)) || 0);
      if (ts && Date.now() - ts < NAG_COOLDOWN_MS) return;
    } catch {
      // localStorage disabled — fall through and show anyway
    }
    setPlatform(detectPlatform());
    // Capture the Android install event
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    // Also show on iOS after a brief settle (no event API available)
    let t;
    if (detectPlatform() === "ios") {
      t = setTimeout(() => setVisible(true), 1200);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (t) clearTimeout(t);
    };
  }, [user]);

  const dismiss = () => {
    try {
      localStorage.setItem(dismissKeyFor(user?.id), String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const androidInstall = async () => {
    if (!deferredPrompt) {
      // Fallback: just dismiss — Chrome's browser menu still has "Install app"
      dismiss();
      return;
    }
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    dismiss();
  };

  if (!visible) return null;

  // Chip sits above the bottom nav (h ~72px + safe area) — never blocks
  // primary work surfaces because it's a self-contained card with its own
  // close button.
  return (
    <div
      data-testid="a2hs-chip"
      className="fixed left-3 right-3 z-40"
      style={{ bottom: "calc(5.25rem + env(safe-area-inset-bottom))" }}
    >
      <div className="bi-surface border border-[#FF9500]/40 rounded-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] p-3">
        {!expanded ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-heading font-bold leading-tight">
                Install BuildIntel on your phone
              </div>
              <div className="text-[11px] text-white/50 leading-snug">
                One-tap access on-site — no browser tabs to hunt for.
              </div>
            </div>
            <button
              type="button"
              data-testid="a2hs-show-how"
              onClick={() =>
                platform === "android" && deferredPrompt
                  ? androidInstall()
                  : setExpanded(true)
              }
              className="h-9 px-3 rounded-lg bi-accent-btn text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1.5 shrink-0"
            >
              {platform === "android" && deferredPrompt ? (
                <>
                  <Download className="w-3.5 h-3.5" />
                  Install
                </>
              ) : (
                <>Show how</>
              )}
            </button>
            <button
              type="button"
              data-testid="a2hs-dismiss"
              onClick={dismiss}
              aria-label="Dismiss install prompt"
              className="w-9 h-9 rounded-lg bi-ghost-btn inline-flex items-center justify-center shrink-0"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-heading font-bold">
                  Add BuildIntel to your Home Screen
                </div>
                <div className="text-[11px] text-white/50">
                  Fast, offline-ready, opens like a real app.
                </div>
              </div>
              <button
                type="button"
                data-testid="a2hs-dismiss"
                onClick={dismiss}
                aria-label="Dismiss install prompt"
                className="w-9 h-9 rounded-lg bi-ghost-btn inline-flex items-center justify-center shrink-0"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
            {platform === "ios" ? (
              <ol
                data-testid="a2hs-ios-steps"
                className="text-[12px] text-white/80 space-y-2 pl-1"
              >
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">1</span>
                  Tap the <b>Share</b>
                  <Share2 className="inline w-3.5 h-3.5 mx-1 text-[#60A5FA]" />
                  button at the bottom of Safari
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">2</span>
                  Choose <b>Add to Home Screen</b>
                  <Plus className="inline w-3.5 h-3.5 mx-1 text-[#FF9500]" />
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">3</span>
                  Tap <b>Add</b> — the hard-hat icon lands on your home screen
                </li>
              </ol>
            ) : platform === "android" ? (
              <ol
                data-testid="a2hs-android-steps"
                className="text-[12px] text-white/80 space-y-2 pl-1"
              >
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">1</span>
                  Open Chrome&apos;s menu (three dots, top right)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">2</span>
                  Tap <b>Install app</b> or <b>Add to Home screen</b>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-white/[0.06] text-white/70 inline-flex items-center justify-center text-[10px] font-heading font-bold">3</span>
                  Confirm — the hard-hat icon lands on your home screen
                </li>
              </ol>
            ) : (
              <div className="text-[12px] text-white/70">
                Open this page in Chrome or Safari on your phone to install.
              </div>
            )}
            <button
              type="button"
              data-testid="a2hs-close"
              onClick={dismiss}
              className="w-full h-10 rounded-xl bi-ghost-btn text-[11px] font-heading font-bold uppercase tracking-wider"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
